import { Box, ListSubheader, MenuList, Typography } from '@mui/material';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';

import React from 'react';

import { IllustratedMessage } from '../Components/IllustratedMessage';
import { LoadingBlock } from '../Components/LoadingBlock';
import { SubmenuItem } from '../Components/SubmenuItem';
import { DEFAULT_LOADING_ROWS_FOR_LEVEL_THREE } from '../Components/utils';
import { useMenuContext } from '../ContextState/useMenuContext';
import { handleTabOrEscapeToLeaveSubmenu } from '../utils/keyboardNavUtils';

import { DATA_TEST, getDynamicDataTestId } from '../enums';
import { IMenuItem, ISubmenuSection } from '../types';

import { levelThreeStyles } from './LevelThree.styles';
import { getFirstMenuItem, handleFocusBetweenMenuLists } from './utils';

interface ILevelThreeContainerProps {
  submenuSections: ISubmenuSection[];
  headerErrorMessage: string;
  showLoadingIndicator?: boolean;
  showErrorMessage?: boolean;
}
export const LevelThreeContainer: React.FC<ILevelThreeContainerProps> = ({
  submenuSections,
  headerErrorMessage,
  showErrorMessage,
  showLoadingIndicator
}) => {
  const {
    favorites,
    disableFavorites,
    currentLevelOneItem,
    onFavoriteIconClick,
    customErrorMessage,
    currentLevelOneItemRef,
    currentLevelTwoItemRef,
    setCurrentLevelOneItem,
    currentFirstLevelThreeItemRef
  } = useMenuContext();

  const menuListRefs = submenuSections.map(() => React.createRef<HTMLUListElement>());

  React.useEffect(() => {
    const firstMenuItem = getFirstMenuItem(menuListRefs);
    if (firstMenuItem) {
      currentFirstLevelThreeItemRef.current = firstMenuItem;
    }
  }, [menuListRefs]);

  const createHandlers = (item: IMenuItem, listLength: number, itemIndex: number, listIndex: number) => {
    const isFavorited =
      favorites?.find((fav) => fav.id === currentLevelOneItem?.id)?.items.some((fav) => fav.id === item.id) ?? false;

    const handleFavoriteIconClick = async (isItemFavorited: boolean) => {
      await onFavoriteIconClick(isItemFavorited, item);
    };

    const handleMenuClick = () => {
      if (item.onClick) item.onClick();
      // Close the menu when a leaf node is clicked
      setCurrentLevelOneItem(null);
    };

    const handleKeyDown = (event) => {
      handleFocusBetweenMenuLists({
        event,
        itemIndex,
        listLength,
        listIndex,
        menuListRefs
      });
      if (event.key === 'ArrowLeft') {
        // Only go left if none of the inner items are currently focused
        if (!event.currentTarget?.querySelector(':focus')) {
          currentLevelTwoItemRef.current?.focus();
        }
      }
      handleTabOrEscapeToLeaveSubmenu(event.key, currentLevelOneItemRef?.current);
    };

    return { isFavorited, handleFavoriteIconClick, handleMenuClick, handleKeyDown };
  };
  return (
    <Box
      data-test={DATA_TEST.submenu_level_three_container}
      sx={levelThreeStyles.container(showErrorMessage || showLoadingIndicator || submenuSections?.length > 0)}
      height="100%"
      overflow="scroll"
    >
      {(() => {
        if (showErrorMessage) {
          return (
            <IllustratedMessage
              illustrationType="error-loading"
              dataTest={DATA_TEST.level_three_error_state}
              headerText={headerErrorMessage}
              messageText={customErrorMessage ?? ''}
              illustrationHeight={139}
              illustrationWidth={239}
            />
          );
        }

        if (showLoadingIndicator) {
          return (
            <Box
              data-test={DATA_TEST.level_three_loading_menu}
              sx={{
                display: 'flex',
                flexFlow: 'wrap',
                width: '100%',
                padding: '8px'
              }}
            >
              <LoadingBlock numberOfRows={DEFAULT_LOADING_ROWS_FOR_LEVEL_THREE} />
            </Box>
          );
        }

        return (
          <Box sx={{ width: '100%' }}>
            {
              <ResponsiveMasonry>
                <Masonry>
                  {submenuSections.map((submenuSection: ISubmenuSection, sectionIdx: number) => {
                    return (
                      <MenuList
                        aria-label={submenuSection.title}
                        ref={menuListRefs[sectionIdx]}
                        sx={levelThreeStyles.menuSection}
                        disableListWrap={true}
                        data-test={getDynamicDataTestId(
                          DATA_TEST.submenu_level_three_container_section,
                          `${sectionIdx}`
                        )}
                        key={`menuList-${submenuSection.title}-${submenuSection.items?.[0]?.id}`}
                        subheader={
                          submenuSection.title && (
                            <ListSubheader
                              sx={{ padding: '0px !important' }}
                              data-selector={'list_subheader'}
                              role="presentation"
                            >
                              <Typography py={1} px={2} variant="h5" role="presentation">
                                {submenuSection.title}
                              </Typography>
                            </ListSubheader>
                          )
                        }
                      >
                        {submenuSection.items?.map((item: IMenuItem, itemIndex: number) => {
                          const { isFavorited, handleFavoriteIconClick, handleMenuClick, handleKeyDown } =
                            createHandlers(item, submenuSection.items?.length ?? 0, itemIndex, sectionIdx);
                          return (
                            <SubmenuItem
                              item={item}
                              key={item.id}
                              isSelected={false}
                              isFavorited={isFavorited}
                              handleKeyDown={handleKeyDown}
                              handleMenuClick={handleMenuClick}
                              handleFavoriteIconClick={handleFavoriteIconClick}
                              lightFontWeight={true}
                              icon={disableFavorites ? undefined : 'favorite'}
                            />
                          );
                        })}
                      </MenuList>
                    );
                  })}
                </Masonry>
              </ResponsiveMasonry>
            }
          </Box>
        );
      })()}
    </Box>
  );
};
